import {
  Alert,
  Button,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@fountain/fountain-ui-components';
import { ExternalLinkOutlineIcon, Icon } from '@fountain/ripple/icon';
import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  CancelablePromise,
  DataCollectionFormField,
  DataCollectionFormResponse,
  DataCollectionFormUpdate,
  OpenAPI,
  WorkflowDataCollectionStage,
  WorkflowEditorService,
} from 'api-clients/monolith';
import { request as __request } from 'api-clients/monolith/core/request';
import produce from 'immer';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { ConfirmationModal } from 'components/ConfirmationModal';
import TextEditor from 'components/TextEditor/Loadable';
import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';
import { useApiService } from 'hooks/useApiService';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import { needsMultipart, toFormDataEntries } from './formData';
import { messages } from './messages';
import { LogicConfirmationKind, QuestionCard } from './QuestionCard';
import { useStyles } from './styles';
import {
  canConfirmAnswer,
  changedLogicJumps,
  destroyedLogicJumps,
  EditableQuestion,
  isOptionable,
  KEY_FORMAT,
  MAX_YEARS_OF_VALIDATION,
  MIN_YEARS_OF_VALIDATION,
  moveCrossesLogic,
  moveShownQuestion,
  QuestionErrors,
  requiresYearsOfValidation,
} from './types';

export interface EditQuestionsModalProps {
  stage: WorkflowDataCollectionStage;
  onClose: () => void;
}

type FormState = {
  formTitle: string;
  formDescription: string;
  questions: EditableQuestion[];
};

type SaveError = { errors?: Record<string, string[]> };

type ConfirmationKind = LogicConfirmationKind | 'move';

type PendingConfirmation = {
  kind: ConfirmationKind;
  onConfirm: () => void;
};

const CONFIRMATION_COPY: Record<
  ConfirmationKind,
  {
    title: keyof typeof messages;
    body: keyof typeof messages;
    confirm: keyof typeof messages;
    negative: boolean;
  }
> = {
  removeQuestion: {
    title: 'removeQuestionLogicTitle',
    body: 'removeQuestionLogicBody',
    confirm: 'removeQuestionLogicConfirm',
    negative: true,
  },
  changeType: {
    title: 'changeTypeLogicTitle',
    body: 'changeTypeLogicBody',
    confirm: 'changeTypeLogicConfirm',
    negative: false,
  },
  linkBank: {
    title: 'linkBankLogicTitle',
    body: 'linkBankLogicBody',
    confirm: 'linkBankLogicConfirm',
    negative: false,
  },
  unlinkBank: {
    title: 'unlinkBankLogicTitle',
    body: 'unlinkBankLogicBody',
    confirm: 'unlinkBankLogicConfirm',
    negative: false,
  },
  removeOption: {
    title: 'removeOptionLogicTitle',
    body: 'removeOptionLogicBody',
    confirm: 'removeOptionLogicConfirm',
    negative: true,
  },
  move: {
    title: 'moveLogicTitle',
    body: 'moveLogicBody',
    confirm: 'moveLogicConfirm',
    negative: false,
  },
};

/** A field as the save sends it: the API shape plus the image file. */
type FieldPayload = DataCollectionFormField & { image?: File };

type FormPayload = Omit<DataCollectionFormUpdate, 'data_fields'> & {
  data_fields: FieldPayload[];
};

const FORM_PATH =
  '/internal_api/workflow_editor/stages/{stage_external_id}/data_collection_form';

const toEditable = (field: DataCollectionFormField): EditableQuestion => {
  const {
    options,
    restricted,
    _destroy,
    logic_jumps_attributes: logicJumps,
    remove_image: removeImage,
    ...rest
  } = field;
  return {
    ...rest,
    uid: String(field.id ?? uuid()),
    options: (options as EditableQuestion['options']) ?? null,
    restricted: restricted ?? null,
    _destroy: Boolean(_destroy),
    logic_jumps_attributes: logicJumps ?? [],
    loaded_logic_jumps: logicJumps ?? [],
    loaded_format: field.format ?? null,
    remove_image: Boolean(removeImage),
  };
};

const newQuestion = (): EditableQuestion => ({
  uid: uuid(),
  key: '',
  type: 'text_field',
  question: '',
  predefined: false,
  required: false,
  options: null,
  restricted: null,
  logic_jumps_attributes: [],
  loaded_logic_jumps: [],
});

/**
 * Turns editor rows into the nested-attributes payload the stage accepts. A
 * removed row that was never saved is simply dropped; a saved one is sent with
 * `_destroy`. Order in the array is the position the server assigns.
 */
const toPayload = (state: FormState): FormPayload => ({
  form_title: state.formTitle,
  form_description: state.formDescription,
  data_fields: state.questions
    .filter(question => !(question._destroy && !question.id))
    .map(question => {
      const custom = !question.predefined;
      const jumps = changedLogicJumps(question);
      return {
        id: question.id ?? undefined,
        key: question.key,
        type: question.type,
        question: question.question,
        hint: question.hint ?? undefined,
        predefined: question.predefined,
        required: Boolean(question.required),
        options:
          custom && isOptionable(question.type)
            ? (question.options ?? [])
            : undefined,
        // Null, not undefined: binding a choice question to a standard key drops
        // its bank, and an omitted key leaves the old association in place.
        option_bank_id: question.option_bank_id ?? null,
        restricted:
          custom && question.restricted !== null
            ? question.restricted
            : undefined,
        // The definition owns a standard question's format; the server ignores
        // a format sent for one, so only a custom question carries it. Null,
        // not undefined, on every custom question: a type change clears these
        // in state, and an omitted key leaves the old value in `extra`, where
        // the portal and the WX attribute mirror still read it.
        format: custom
          ? question.type === 'text_field'
            ? (question.format ?? null)
            : null
          : undefined,
        format_validation_message: custom
          ? question.type === 'text_field'
            ? (question.format_validation_message ?? null)
            : null
          : undefined,
        min_date: custom
          ? question.type === 'datepicker'
            ? (question.min_date ?? null)
            : null
          : undefined,
        max_date: custom
          ? question.type === 'datepicker'
            ? (question.max_date ?? null)
            : null
          : undefined,
        // False, not omitted, once a custom question's type cannot be
        // confirmed: `confirm` is a column, and an omitted key keeps it set.
        confirm: canConfirmAnswer(question)
          ? Boolean(question.confirm)
          : custom
            ? false
            : undefined,
        hidden_value:
          question.type === 'hidden_field'
            ? (question.hidden_value ?? '')
            : undefined,
        years_of_validation: requiresYearsOfValidation(question.type)
          ? (question.years_of_validation ?? undefined)
          : undefined,
        logic_jumps_attributes: jumps.length > 0 ? jumps : undefined,
        image: question.image ?? undefined,
        remove_image: question.remove_image ? true : undefined,
        _destroy: question._destroy ? true : undefined,
      };
    }),
});

const validRegex = (format: string): boolean => {
  try {
    new RegExp(format);
    return true;
  } catch {
    return false;
  }
};

const validate = (
  questions: EditableQuestion[],
  intl: ReturnType<typeof useIntl>,
): Record<string, QuestionErrors> => {
  const live = questions.filter(question => !question._destroy);
  const keyCounts: Record<string, number> = {};
  live.forEach(question => {
    if (question.key)
      keyCounts[question.key] = (keyCounts[question.key] ?? 0) + 1;
  });

  const result: Record<string, QuestionErrors> = {};
  live.forEach(question => {
    const errors: QuestionErrors = {};
    if (!question.question.trim()) {
      errors.question = intl.formatMessage(messages.errorQuestionRequired);
    }
    if (!question.key) {
      errors.key = intl.formatMessage(messages.errorKeyRequired);
    } else if (!KEY_FORMAT.test(question.key)) {
      errors.key = intl.formatMessage(messages.errorKeyFormat);
    } else if (keyCounts[question.key] > 1) {
      errors.key = intl.formatMessage(messages.errorKeyDuplicate);
    }
    if (
      !question.predefined &&
      isOptionable(question.type) &&
      !question.option_bank_id &&
      (question.options ?? []).filter(
        option => option.visible !== false && option.value.trim(),
      ).length === 0
    ) {
      errors.options = intl.formatMessage(messages.errorOptionsRequired);
    }
    if (
      !question.predefined &&
      question.type === 'datepicker' &&
      question.min_date &&
      question.max_date &&
      question.min_date > question.max_date
    ) {
      errors.dateRange = intl.formatMessage(messages.errorDateRange);
    }
    // Only a pattern typed here is checked in the browser's dialect: a stored
    // one may use syntax only Ruby reads, and the server refuses one Ruby
    // cannot compile.
    if (
      !question.predefined &&
      question.type === 'text_field' &&
      question.format &&
      question.format !== question.loaded_format &&
      !validRegex(question.format)
    ) {
      errors.format = intl.formatMessage(messages.errorFormatInvalid);
    }
    if (
      question.type === 'hidden_field' &&
      !(question.hidden_value ?? '').trim()
    ) {
      errors.hiddenValue = intl.formatMessage(
        messages.errorHiddenValueRequired,
      );
    }
    if (requiresYearsOfValidation(question.type)) {
      const years = question.years_of_validation;
      if (
        years == null ||
        !Number.isInteger(years) ||
        years < MIN_YEARS_OF_VALIDATION ||
        years > MAX_YEARS_OF_VALIDATION
      ) {
        errors.yearsOfValidation = intl.formatMessage(
          messages.errorYearsOfValidation,
          { min: MIN_YEARS_OF_VALIDATION, max: MAX_YEARS_OF_VALIDATION },
        );
      }
    }
    if (Object.keys(errors).length) result[question.uid] = errors;
  });
  return result;
};

/**
 * A save with an image travels as multipart; the generated client only knows
 * the JSON body, so that path builds the same request by hand.
 */
const saveForm = (
  stageExternalId: string,
  payload: FormPayload,
  multipart: boolean,
): CancelablePromise<DataCollectionFormResponse> =>
  multipart
    ? __request<DataCollectionFormResponse>(OpenAPI, {
        method: 'PATCH',
        url: FORM_PATH,
        path: { stage_external_id: stageExternalId },
        formData: toFormDataEntries(payload),
        errors: { 422: 'unprocessable entity' },
      })
    : WorkflowEditorService.patchInternalApiWorkflowEditorStagesDataCollectionForm(
        stageExternalId,
        payload,
      );

export const EditQuestionsModal: FC<EditQuestionsModalProps> = ({
  stage,
  onClose,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const { accountSlug } = useParams<{ accountSlug: string }>();
  const { refetchStage } = useContext(StageContext);

  const { result } = useApiService(
    useCallback(
      () =>
        WorkflowEditorService.getInternalApiWorkflowEditorStagesDataCollectionForm(
          stage.external_id,
        ),
      [stage.external_id],
    ),
  );

  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, QuestionErrors>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(
    null,
  );

  useEffect(() => {
    if (result.status === 'ready' && form === null) {
      const data: DataCollectionFormResponse = result.data;
      setForm({
        formTitle: data.form.form_title ?? '',
        formDescription: data.form.form_description ?? '',
        questions: data.form.data_fields.map(toEditable),
      });
    }
  }, [result, form]);

  const { mutation: save, result: saveResult } = useApiServiceMutation<
    DataCollectionFormResponse,
    typeof saveForm,
    SaveError
  >(saveForm, {
    onSuccess: () => {
      refetchStage();
      onClose();
    },
    onError: error => {
      const detail = error?.errors
        ? Object.values(error.errors).flat().join(' ')
        : '';
      setServerError(
        [intl.formatMessage(messages.saveError), detail]
          .filter(Boolean)
          .join(' '),
      );
    },
  });

  const updateQuestion = (uid: string, patch: Partial<EditableQuestion>) =>
    setForm(
      produce(draft => {
        if (!draft) return;
        const target = draft.questions.find(question => question.uid === uid);
        if (target) Object.assign(target, patch);
      }),
    );

  const removeQuestion = (uid: string) =>
    setForm(
      produce(draft => {
        if (!draft) return;
        const index = draft.questions.findIndex(
          question => question.uid === uid,
        );
        if (index === -1) return;
        const target = draft.questions[index];
        if (target.id) {
          target._destroy = true;
          target.logic_jumps_attributes = destroyedLogicJumps(target);
        } else {
          draft.questions.splice(index, 1);
        }
      }),
    );

  const addQuestion = () =>
    setForm(
      produce(draft => {
        draft?.questions.push(newQuestion());
      }),
    );

  const moveQuestion = (from: number, to: number) =>
    setForm(
      produce(draft => {
        if (!draft) return;
        draft.questions = moveShownQuestion(draft.questions, from, to);
      }),
    );

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination || destination.index === source.index || !form) return;
    const shown = form.questions.filter(
      question => !question._destroy && !question.hidden,
    );
    const move = () => moveQuestion(source.index, destination.index);
    if (moveCrossesLogic(shown, source.index, destination.index)) {
      setConfirmation({ kind: 'move', onConfirm: move });
    } else {
      move();
    }
  };

  const onSave = () => {
    if (!form) return;
    const nextErrors = validate(form.questions, intl);
    setErrors(nextErrors);
    setServerError(null);
    if (Object.keys(nextErrors).length) return;
    save(stage.external_id, toPayload(form), needsMultipart(form.questions));
  };

  const templates =
    result.status === 'ready' ? result.data.standard_questions : [];
  const optionBanks = result.status === 'ready' ? result.data.option_banks : [];
  const settings = result.status === 'ready' ? result.data.settings : null;
  const brandSlug =
    result.status === 'ready' ? result.data.form.brand_slug : null;
  const canRestrict = Boolean(
    settings?.sensitive_keys_enabled && settings?.access_secure_data,
  );
  const liveQuestions =
    form?.questions.filter(question => !question._destroy) ?? [];
  // A hidden standard question (the BIC companion of an IBAN) stays in the
  // payload so the server keeps it, and never renders.
  const shownQuestions = liveQuestions.filter(question => !question.hidden);
  const usedKeys = liveQuestions.map(question => question.key).filter(Boolean);
  const blockerCount = Object.keys(errors).length;
  const saving = saveResult.status === 'loading';
  const confirmationCopy = confirmation && CONFIRMATION_COPY[confirmation.kind];

  return (
    <Modal
      open
      onClose={onClose}
      ariaLabelledBy="edit-questions-modal-title"
      maxWidth={880}
      fullScreenOnMobile
    >
      <ModalHeader
        onClose={onClose}
        ariaLabelledBy="edit-questions-modal-title"
        showIcon={false}
      >
        {intl.formatMessage(messages.title)}
      </ModalHeader>
      <ModalContent>
        <div className={styles.content}>
          {result.status === 'error' && (
            <Alert type="danger" fullWidth>
              <Typography variant="body2" component="span">
                {intl.formatMessage(messages.loadError)}
              </Typography>
            </Alert>
          )}
          {serverError && (
            <Alert type="danger" fullWidth>
              <Typography variant="body2" component="span">
                {serverError}
              </Typography>
            </Alert>
          )}
          {blockerCount > 0 && (
            <Alert type="danger" fullWidth>
              <Typography variant="body2" component="span">
                {intl.formatMessage(messages.blockerCount, {
                  count: blockerCount,
                })}
              </Typography>
            </Alert>
          )}

          {form === null && result.status !== 'error' && (
            <>
              <Skeleton variant="rect" height={48} />
              <Skeleton variant="rect" className={styles.skeletonCard} />
              <Skeleton variant="rect" className={styles.skeletonCard} />
            </>
          )}

          {form && settings && (
            <>
              <div className={styles.formHeader}>
                <Input
                  fullWidth
                  label={intl.formatMessage(messages.formTitle)}
                  value={form.formTitle}
                  inputProps={{
                    'aria-label': intl.formatMessage(messages.formTitle),
                  }}
                  onChange={event => {
                    const formTitle = event.target.value;
                    setForm(current => current && { ...current, formTitle });
                  }}
                />
                <div>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={styles.fieldLabel}
                  >
                    {intl.formatMessage(messages.formDescription)}
                  </Typography>
                  <TextEditor
                    id={`data-collection-form-description-${stage.external_id}`}
                    value={form.formDescription}
                    placeholder={intl.formatMessage(
                      messages.formDescriptionPlaceholder,
                    )}
                    onChange={(formDescription: string) => {
                      setForm(
                        current => current && { ...current, formDescription },
                      );
                    }}
                    disableImageUpload
                    enableLinkButton
                  />
                </div>
              </div>

              <div>
                <div className={styles.sectionHeader}>
                  <Typography variant="h4">
                    {intl.formatMessage(messages.questions)}
                  </Typography>
                  {settings.logic_jumps_enabled && brandSlug && (
                    <div>
                      {/* Same-origin page, so the referrer stays: the preview
                          gateway attributes the new tab's first load by it. */}
                      <Link
                        to={`/${accountSlug}/brands/${brandSlug}/stages/${stage.external_id}/logic`}
                        target="_blank"
                        rel="noopener"
                        className={styles.logicJumpsLink}
                      >
                        {intl.formatMessage(messages.configureLogicJumps)}
                        <Icon
                          icon={ExternalLinkOutlineIcon}
                          size="xs"
                          color="inherit"
                        />
                      </Link>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        {intl.formatMessage(
                          messages.configureLogicJumpsDescription,
                        )}
                      </Typography>
                    </div>
                  )}
                </div>
                {shownQuestions.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Typography variant="h5">
                      {intl.formatMessage(messages.noQuestionsTitle)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {intl.formatMessage(messages.noQuestionsDescription)}
                    </Typography>
                  </div>
                ) : (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="edit-questions-list">
                      {provided => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={styles.questionList}
                        >
                          {shownQuestions.map((question, index) => (
                            <Draggable
                              key={question.uid}
                              draggableId={question.uid}
                              index={index}
                            >
                              {draggableProvided => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                >
                                  <QuestionCard
                                    question={question}
                                    errors={errors[question.uid]}
                                    templates={templates}
                                    optionBanks={optionBanks}
                                    settings={settings}
                                    usedKeys={usedKeys}
                                    canRestrict={canRestrict}
                                    dragHandleProps={
                                      draggableProvided.dragHandleProps
                                    }
                                    onChange={patch =>
                                      updateQuestion(question.uid, patch)
                                    }
                                    onRemove={() =>
                                      removeQuestion(question.uid)
                                    }
                                    requestConfirmation={(kind, onConfirm) =>
                                      setConfirmation({ kind, onConfirm })
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>

              <div>
                <Button
                  type="secondary"
                  size="small"
                  autoWidth
                  onClick={addQuestion}
                >
                  {intl.formatMessage(messages.addQuestion)}
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalContent>
      <ModalFooter className={styles.footer}>
        <Button type="secondary" size="small" autoWidth onClick={onClose}>
          {intl.formatMessage(globalMessages.cancel)}
        </Button>
        <Button
          type="primary"
          size="small"
          autoWidth
          isLoading={saving}
          disabled={form === null}
          onClick={onSave}
        >
          {intl.formatMessage(messages.saveQuestions)}
        </Button>
      </ModalFooter>
      {confirmation && confirmationCopy && (
        <ConfirmationModal
          isOpen
          title={intl.formatMessage(messages[confirmationCopy.title])}
          bodyContent={intl.formatMessage(messages[confirmationCopy.body])}
          cancelButtonContent={intl.formatMessage(globalMessages.cancel)}
          confirmButtonContent={intl.formatMessage(
            messages[confirmationCopy.confirm],
          )}
          negative={confirmationCopy.negative}
          maxWidth={440}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            confirmation.onConfirm();
            setConfirmation(null);
          }}
        />
      )}
    </Modal>
  );
};
